<template>
  <div class="samba-vast-player" :class="{ 'video-loading': internalLoading }">
    <div class="video-container">
      <div v-if="internalLoading" class="video-loading-placeholder">
        <div class="loading-spinner" />
      </div>
      <video
        :id="playerId"
        ref="videoPlayer"
        class="video-js vjs-default-skin vjs-big-play-centered"
        preload="auto"
        playsinline
        muted
        :style="{ width: width, height: height }"
      />
      <div v-if="showFooterText && adType === 'pre'" class="advertising-overlay">
        <div class="advertising-label" :style="{ color: advertisingTextColor }">
          {{ $t('advertising') }}
        </div>
      </div>
      <div v-if="showFooterText && adType === 'pre'" class="player-footer-text">
        <div class="redirect-message" :style="{ color: advertisingTextColor }">
          {{ $t('ad_redirect_message') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import videojs from 'video.js'
  import 'video.js/dist/video-js.css'
  import { sendError } from '../../utils/logging'
  import axios from 'axios'
  import debugLogger from '../../utils/DebugLoggerSDK'
  import { isDesktop, isMobile } from '../../helpers'

  export default {
    name: 'SambaVastPlayer',
    props: {
      vastTagUrl: {
        type: String,
        required: true,
      },
      playerId: {
        type: String,
        required: true,
      },
      width: {
        type: [String, Number],
        default: '100%',
      },
      height: {
        type: [String, Number],
        default: 'auto',
      },
      allowSkip: {
        type: Boolean,
        default: true,
      },
      skipDelay: {
        type: Number,
        default: 15,
      },
      adType: {
        type: String,
        default: 'post',
      },
    },

    data() {
      return {
        player: null,
        adStarted: false,
        adCompleted: false,
        internalLoading: true,
        hasError: false,
        errorMessage: null,
        firstQuartileFired: false,
        midpointFired: false,
        thirdQuartileFired: false,
        currentCreative: null,
        trackingEvents: null,
        sambaAdData: null,
        mediaUrl: null,
        analyticsEvents: null,
        thePlayerConfig: {
          controls: true,
          autoplay: 'muted',
          preload: 'auto',
          fluid: true,
          responsive: true,
          muted: true,
          playsinline: true,
          controlBar: {
            pictureInPictureToggle: false,
          },
        },
        fullscreenTrackingFired: false,
        showFooterText: false,
      }
    },

    computed: {
      baseData() {
        return this.$store?.state?.baseData || {}
      },
      advertisingTextColor() {
        return this.baseData?.colors?.textColor || '#202020'
      },
    },

    mounted() {
      this.initializePlayer()
    },

    beforeDestroy() {
      if (this.player) {
        this.player.dispose()
      }
    },

    methods: {
      async fetchSambaAdData(vastUrl) {
        try {
          debugLogger.info('Starting Samba ad data extraction', { vastUrl })
          sendError({
            type: 'SAMBA_AD_DATA_START',
            message: 'Starting Samba ad data extraction',
            vastUrl,
            userAgent: navigator.userAgent,
            location: window.location.href,
            timestamp: new Date().toISOString(),
          })

          const extractUrl = `/index.php?controller=proxy-request&action=extractSambaAdsData&url=${encodeURIComponent(
            vastUrl,
          )}`

          const response = await axios.get(extractUrl, {
            timeout: 10000,
            withCredentials: false,
          })

          debugLogger.success('Samba ad data received', {
            status: response.status,
            hasData: !!response.data,
            url: response.config.url,
          })

          sendError({
            type: 'SAMBA_AD_DATA_RESPONSE',
            message: 'Samba ad data response received',
            vastUrl,
            status: response.status,
            statusText: response.statusText,
            hasData: !!response.data,
            timestamp: new Date().toISOString(),
          })

          if (response.data && response.data.error === true) {
            debugLogger.warn('Backend returned error flag - Samba server unreachable', {
              vastUrl,
              responseData: response.data,
            })
            sendError({
              type: 'SAMBA_SERVER_UNREACHABLE',
              message: 'Samba server unreachable - backend returned error flag',
              vastUrl,
              status: response.status,
              timestamp: new Date().toISOString(),
            })
            return null
          }

          if (response.status === 500) {
            debugLogger.warn('Backend returned 500 - Samba server may be unreachable', {
              vastUrl,
              status: response.status,
            })
            sendError({
              type: 'SAMBA_SERVER_ERROR_500',
              message: 'Backend returned 500 - Samba server may be unreachable',
              vastUrl,
              status: response.status,
              timestamp: new Date().toISOString(),
            })
            return null
          }

          if (!response.data) {
            debugLogger.error('No data in Samba ad response', { vastUrl })
            sendError({
              type: 'SAMBA_AD_DATA_FAILED',
              message: 'Samba ad data extraction failed - no data in response',
              vastUrl,
              status: response.status,
              timestamp: new Date().toISOString(),
            })
            return null
          }

          debugLogger.success('Samba ad data extracted successfully', {
            vastUrl,
            hasMediaUrl: !!response.data.media_url,
            hasAnalytics: !!response.data.analytics,
          })

          sendError({
            type: 'SAMBA_AD_DATA_SUCCESS',
            message: 'Samba ad data extracted successfully',
            vastUrl,
            hasMediaUrl: !!response.data.media_url,
            hasAnalytics: !!response.data.analytics,
            timestamp: new Date().toISOString(),
          })

          return response.data
        } catch (error) {
          const isNetworkError =
            error.code === 'ECONNREFUSED' ||
            error.code === 'ETIMEDOUT' ||
            error.code === 'ENOTFOUND' ||
            error.message?.includes('Failed to connect') ||
            error.message?.includes('Network Error') ||
            (error.response && error.response.status >= 500)

          debugLogger.error('Samba ad data extraction failed', {
            message: error.message,
            code: error.code,
            name: error.name,
            isNetworkError,
            status: error.response?.status,
            vastUrl,
          })

          sendError({
            type: 'SAMBA_AD_DATA_ERROR',
            message: 'Samba ad data extraction error caught',
            vastUrl,
            error: error.message,
            errorName: error.name,
            errorCode: error.code,
            isNetworkError,
            status: error.response?.status,
            stack: error.stack,
            timestamp: new Date().toISOString(),
          })

          if (isNetworkError) {
            debugLogger.warn('Samba server unreachable - handling gracefully', { vastUrl })
            return null
          }

          return null
        }
      },

      getStreamUrl(mediaUrl, mediaType = 'video/mp4') {
        try {
          debugLogger.info('Getting stream URL from backend', {
            mediaUrl,
            mediaType,
            currentDomain: window.location.hostname,
            protocol: window.location.protocol,
            userAgent: navigator.userAgent,
          })

          const streamUrl = `/index.php?controller=proxy-request&action=streamContent&from=${encodeURIComponent(
            mediaUrl,
          )}&type=${encodeURIComponent(mediaType)}`

          debugLogger.success('Stream URL constructed', {
            originalMediaUrl: mediaUrl,
            mediaType,
            streamUrl,
            fullUrl: window.location.origin + streamUrl,
          })

          return streamUrl
        } catch (error) {
          debugLogger.error('Failed to construct stream URL', {
            message: error.message,
            mediaUrl,
            mediaType,
          })
          throw error
        }
      },

      async initializePlayer() {
        try {
          debugLogger.info('Starting player initialization', { vastTagUrl: this.vastTagUrl })

          this.resetTrackingState()
          this.hasError = false
          this.errorMessage = null

          debugLogger.info('VideoJS player preparations')
          this.player = videojs(this.$refs.videoPlayer, this.thePlayerConfig)

          debugLogger.info('Fetching Samba ad data...')
          this.sambaAdData = await this.fetchSambaAdData(this.vastTagUrl)

          if (!this.sambaAdData) {
            debugLogger.warn('No Samba ad data received - Samba server may be unreachable', {
              vastTagUrl: this.vastTagUrl,
            })
            sendError({
              type: 'NO_SAMBA_AD_DATA',
              message: 'No Samba ad data received - handling gracefully',
              vastTagUrl: this.vastTagUrl,
              timestamp: new Date().toISOString(),
            })

            this.hasError = true
            this.errorMessage = 'Samba server unreachable'
            this.internalLoading = false

            this.$emit('ad-error', new Error('Samba server unreachable'))
            return
          }

          debugLogger.success('Samba ad data received', {
            hasMediaUrl: !!this.sambaAdData.media_url,
            hasAnalytics: !!this.sambaAdData.analytics,
          })

          this.analyticsEvents = this.sambaAdData.analytics || {}
          this.trackingEvents = this.analyticsEvents

          if (!this.sambaAdData.media_url) {
            debugLogger.error('No media URL in Samba ad data', {
              vastTagUrl: this.vastTagUrl,
              sambaAdData: this.sambaAdData,
            })
            sendError({
              type: 'NO_MEDIA_URL',
              message: 'No media URL in Samba ad data',
              vastTagUrl: this.vastTagUrl,
              sambaAdData: this.sambaAdData,
              timestamp: new Date().toISOString(),
            })
            this.$emit('error', new Error('No media URL found'))
            this.internalLoading = false
            return
          }

          this.mediaUrl = this.getStreamUrl(this.sambaAdData.media_url, this.sambaAdData.media_type)

          debugLogger.info('Starting to play Samba ad', {
            originalMediaUrl: this.sambaAdData.media_url,
            streamUrl: this.mediaUrl,
            analyticsEvents: Object.keys(this.analyticsEvents),
          })

          this.playSambaAd()
        } catch (error) {
          debugLogger.error('Player initialization error', {
            message: error.message,
            name: error.name,
            vastTagUrl: this.vastTagUrl,
          })
          sendError({
            type: 'PLAYER_INIT_ERROR',
            message: 'Player initialization error',
            vastTagUrl: this.vastTagUrl,
            error: error.message,
            errorName: error.name,
            stack: error.stack,
            timestamp: new Date().toISOString(),
          })

          this.hasError = true
          this.errorMessage = error.message || 'Failed to initialize player'
          this.internalLoading = false

          this.$emit('ad-error', error)
        }
      },

      playSambaAd() {
        try {
          debugLogger.info('Setting up Samba ad playback', {
            streamUrl: this.mediaUrl,
            analyticsEvents: Object.keys(this.analyticsEvents),
          })

          try {
            debugLogger.info('Setting video source', {
              streamUrl: this.mediaUrl,
              fullUrl: window.location.origin + this.mediaUrl,
              networkInfo: {
                online: navigator.onLine,
                connectionType: navigator.connection?.effectiveType || 'unknown',
                downlink: navigator.connection?.downlink || 'unknown',
              },
            })

            this.player.src({
              type: this.sambaAdData.media_type || 'video/mp4',
              src: this.mediaUrl,
            })

            debugLogger.success('Video source set successfully', { streamUrl: this.mediaUrl })
          } catch (error) {
            sendError({
              type: 'VIDEO_SOURCE_SET_ERROR',
              message: 'Error setting video source',
              streamUrl: this.mediaUrl,
              error: error.message,
              errorName: error.name,
              stack: error.stack,
              timestamp: new Date().toISOString(),
            })
            throw error
          }

          this.setupAnalyticsEvents()
          this.setupPlayerEvents()
          this.setupClickThrough()
          this.setupSkipButton()
          this.disableSeeking()

          this.player.on('error', (error) => {
            const videoError = this.player.error()
            debugLogger.error('Video player error', {
              error,
              videoError,
              errorCode: videoError?.code,
              errorMessage: videoError?.message,
              streamUrl: this.mediaUrl,
              fullUrl: window.location.origin + this.mediaUrl,
              networkInfo: {
                online: navigator.onLine,
                connectionType: navigator.connection?.effectiveType || 'unknown',
              },
              playerReadyState: this.player.readyState(),
              networkState: this.player.networkState(),
            })

            if (this.sambaAdData?.media_url && this.mediaUrl.includes('proxy-request')) {
              debugLogger.warn('Proxy failed, trying direct Samba URL', {
                originalUrl: this.sambaAdData.media_url,
                proxyUrl: this.mediaUrl,
              })

              try {
                this.player.src({
                  type: 'video/mp4',
                  src: this.sambaAdData.media_url,
                })
                debugLogger.success('Fallback to direct URL successful', {
                  directUrl: this.sambaAdData.media_url,
                })
                return
              } catch (fallbackError) {
                debugLogger.error('Fallback to direct URL also failed', {
                  fallbackError: fallbackError.message,
                  directUrl: this.sambaAdData.media_url,
                })
              }
            }

            sendError({
              type: 'VIDEO_PLAYER_ERROR',
              message: 'Video player error - media could not be loaded',
              streamUrl: this.mediaUrl,
              error: error.message || 'Unknown video error',
              networkInfo: {
                online: navigator.onLine,
                connectionType: navigator.connection?.effectiveType || 'unknown',
              },
              timestamp: new Date().toISOString(),
            })
          })

          this.player.on('loadstart', () => {
            debugLogger.info('Video load started', { streamUrl: this.mediaUrl })
          })

          this.player.on('loadedmetadata', () => {
            const videoElement = this.player.el().querySelector('video')
            if (videoElement) {
              const videoWidth = videoElement.videoWidth
              const videoHeight = videoElement.videoHeight

              debugLogger.info('Video metadata loaded', {
                duration: this.player.duration(),
                videoWidth,
                videoHeight,
                streamUrl: this.mediaUrl,
              })

              if (videoWidth > 0 && videoHeight > 0) {
                const aspectRatio = `${videoWidth}:${videoHeight}`
                this.player.aspectRatio(aspectRatio)
                debugLogger.info('Set dynamic aspect ratio', { aspectRatio })
              }
            } else {
              debugLogger.info('Video metadata loaded', {
                duration: this.player.duration(),
                streamUrl: this.mediaUrl,
              })
            }

            debugLogger.info('Metadata loaded but keeping loader visible until playing')
          })

          this.player.on('loadeddata', () => {
            debugLogger.info('Video data loaded', { streamUrl: this.mediaUrl })
          })

          this.player.on('canplay', () => {
            debugLogger.success('Video can start playing', { streamUrl: this.mediaUrl })
          })

          this.player.on('loaderror', (error) => {
            debugLogger.error('Video load error event', {
              error,
              streamUrl: this.mediaUrl,
              fullUrl: window.location.origin + this.mediaUrl,
            })
          })

          this.$emit('player-ready')

          this.setupMobileAutoplay()

          this.setupPseudoFullscreen()

          this.attemptAutoplay()

          this.$emit('player-methods', {
            triggerCompleteEvent: this.triggerCompleteEvent,
          })
        } catch (error) {
          sendError({
            type: 'PLAY_SAMBA_AD_ERROR',
            message: 'Error in playSambaAd method',
            error: error.message,
            errorName: error.name,
            stack: error.stack,
            timestamp: new Date().toISOString(),
          })
          this.$emit('error', error)
          this.internalLoading = false
        }
      },

      setupAnalyticsEvents() {
        this.trackingEvents = this.analyticsEvents || {}
        debugLogger.info('Analytics events loaded', this.analyticsEvents)

        if (this.trackingEvents.impression) {
          debugLogger.info('Firing impression events', this.trackingEvents.impression)
          this.trackEvents(this.trackingEvents.impression)
        }

        if (this.trackingEvents.creativeView) {
          debugLogger.info('MyWiFi Player - creativeView')
          debugLogger.info('Firing creativeView events', this.trackingEvents.creativeView)
          this.trackEvents(this.trackingEvents.creativeView)
        } else {
          debugLogger.warn('No creativeView event in analytics')
        }

        this.setupQuartileTracking(this.trackingEvents)
      },

      setupPlayerEvents() {
        this.player.one('playing', () => {
          this.adStarted = true
          this.showFooterText = true

          this.internalLoading = false
          debugLogger.info('Video started playing - hiding loader, showing controls')

          this.$emit('ad-started')
          this.$emit('ad-impression')

          if (this.trackingEvents?.start) {
            debugLogger.info('MyWiFi Player - start')
            debugLogger.info('Firing start events', this.trackingEvents.start)
            this.trackEvents(this.trackingEvents.start)
          } else {
            debugLogger.warn('XML missing event start')
          }
        })

        this.player.on('playing', () => {
          if (this.adStarted) {
            this.showFooterText = true
          }
        })

        this.player.one('ended', () => {
          this.adCompleted = true
          this.$emit('ad-completed')

          if (this.trackingEvents?.complete) {
            debugLogger.info('MyWiFi Player - complete')
            debugLogger.info('Firing complete events', this.trackingEvents.complete)
            this.trackEvents(this.trackingEvents.complete)
          } else {
            debugLogger.warn('XML missing event complete')
          }
        })

        this.player.on('pause', () => {
          if (this.trackingEvents?.pause) {
            debugLogger.info('MyWiFi Player - pause')
            debugLogger.info('Firing pause events', this.trackingEvents.pause)
            this.trackEvents(this.trackingEvents.pause)
          } else {
            debugLogger.warn('XML missing event pause')
          }
        })

        this.player.on('play', () => {
          this.showFooterText = true
          if (this.trackingEvents?.resume) {
            debugLogger.info('MyWiFi Player - resume')
            debugLogger.info('Firing resume events', this.trackingEvents.resume)
            this.trackEvents(this.trackingEvents.resume)
          } else {
            debugLogger.warn('XML missing event resume')
          }
        })

        this.player.on('volumechange', () => {
          if (this.player.muted()) {
            if (this.trackingEvents?.mute) {
              debugLogger.info('MyWiFi Player - mute')
              debugLogger.info('Firing mute events', this.trackingEvents.mute)
              this.trackEvents(this.trackingEvents.mute)
            } else {
              debugLogger.warn('XML missing event mute')
            }
          } else {
            if (this.trackingEvents?.unmute) {
              debugLogger.info('MyWiFi Player - unmute')
              debugLogger.info('Firing unmute events', this.trackingEvents.unmute)
              this.trackEvents(this.trackingEvents.unmute)
            } else {
              debugLogger.warn('XML missing event unmute')
            }
          }
        })

        this.player.on('fullscreenchange', () => {
          debugLogger.info('MyWiFi Player - fullscreenchange')
          if (this.trackingEvents?.fullscreen) {
            debugLogger.info('Firing fullscreen events', this.trackingEvents.fullscreen)
            this.trackEvents(this.trackingEvents.fullscreen)
          } else {
            debugLogger.warn('XML missing event fullscreen')
          }
        })

        this.player.on('error', (error) => {
          sendError({
            type: 'VIDEO_PLAYER_ERROR',
            message: 'Video player error event fired',
            error: error.message,
            errorName: error.name,
            errorCode: error.code,
            errorType: error.type,
            target: error.target,
            timestamp: new Date().toISOString(),
          })
          this.$emit('ad-error', error)
        })
      },

      setupQuartileTracking(trackingEvents) {
        this.player.on('timeupdate', () => {
          if (!this.adStarted) return

          const currentTime = this.player.currentTime()
          const duration = this.player.duration()

          if (duration <= 0) return

          const progress = (currentTime / duration) * 100

          // First quartile (25%)
          if (progress >= 25 && !this.firstQuartileFired) {
            if (trackingEvents?.firstQuartile) {
              this.fireTrackingEvents(trackingEvents.firstQuartile)
              this.firstQuartileFired = true
              debugLogger.info('MyWiFi Player - firstQuartile')
            } else {
              debugLogger.warn('XML missing event firstQuartile')
            }
          }

          // Midpoint (50%)
          if (progress >= 50 && !this.midpointFired) {
            if (trackingEvents?.midpoint) {
              this.fireTrackingEvents(trackingEvents.midpoint)
              this.midpointFired = true
              debugLogger.info('MyWiFi Player - midpoint')
            } else {
              debugLogger.warn('XML missing event midpoint')
            }
          }

          // Third quartile (75%)
          if (progress >= 75 && !this.thirdQuartileFired) {
            if (trackingEvents?.thirdQuartile) {
              this.fireTrackingEvents(trackingEvents.thirdQuartile)
              this.thirdQuartileFired = true
              debugLogger.info('MyWiFi Player - thirdQuartile')
            } else {
              debugLogger.warn('XML missing event thirdQuartile')
            }
          }
        })
      },

      setupClickThrough() {
        // Check if we have click-through data in the analytics
        if (this.analyticsEvents && this.analyticsEvents.clickThrough) {
          // Add click handler to the video player
          this.player.on('click', () => {
            debugLogger.info('Click-through triggered', {
              clickThrough: this.analyticsEvents.clickThrough,
            })
            window.open(this.analyticsEvents.clickThrough, '_blank')

            if (this.analyticsEvents.clickTracking) {
              this.trackEvents([this.analyticsEvents.clickTracking])
            }
          })

          this.player.addClass('vjs-clickable')
        }
      },

      fireTrackingEvents(events) {
        if (events && Array.isArray(events)) {
          this.trackEvents(events)
        }
      },

      trackEvents(urls) {
        if (urls && urls.length > 0) {
          debugLogger.info('Tracking events fired', { urls })

          const trackingUrl = `/index.php?controller=proxy-request&action=trackSambaAdsEvent&urls=${encodeURIComponent(
            JSON.stringify(urls),
          )}`

          axios
            .get(trackingUrl, {
              timeout: 7500,
              withCredentials: false,
            })
            .catch(() => {
              debugLogger.error('Failed to handle the event', {
                urlsCount: urls.length,
                urls,
                backendUrl: trackingUrl,
              })
            })

          debugLogger.info('Tracking events dispatched to backend', {
            urlsCount: urls.length,
            urls,
            backendUrl: trackingUrl,
          })
        }
      },

      resetTrackingState() {
        this.firstQuartileFired = false
        this.midpointFired = false
        this.thirdQuartileFired = false
        this.adStarted = false
        this.adCompleted = false
        this.fullscreenTrackingFired = false
        this.showFooterText = false
      },

      setupSkipButton() {
        if (this.allowSkip && this.trackingEvents?.skip) {
          const skipButton = document.createElement('button')
          skipButton.className = 'vjs-skip-button'
          skipButton.style.cssText = `
          position: absolute;
          top: 10px;
          right: 10px;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          border: none;
          padding: 8px 12px;
          border-radius: 4px;
          cursor: not-allowed;
          z-index: 1000;
          font-size: 12px;
          display: block;
          opacity: 0.7;
        `

          const skipDelayValue = this.skipDelay || 15
          skipButton.innerHTML = `Skip Ad (${skipDelayValue})`
          skipButton.disabled = true
          skipButton.style.display = 'none'

          skipButton.addEventListener('click', () => {
            if (!skipButton.disabled) {
              debugLogger.info('MyWiFi Player - skip')
              this.trackEvents(this.trackingEvents.skip)

              this.player.pause()
              this.$emit('ad-skipped')

              skipButton.style.display = 'none'
            }
          })

          this.player.ready(() => {
            this.player.el().appendChild(skipButton)
          })

          let countdown = skipDelayValue
          let countdownInterval = null
          let hasStarted = false

          const startCountdown = () => {
            if (hasStarted) return
            hasStarted = true

            debugLogger.info('Starting skip button countdown', { skipDelay: skipDelayValue })

            skipButton.style.display = 'block'

            countdownInterval = setInterval(() => {
              countdown--
              skipButton.innerHTML = `Skip Ad (${countdown})`

              if (countdown <= 0) {
                skipButton.innerHTML = 'Skip Ad'
                skipButton.disabled = false
                skipButton.style.cursor = 'pointer'
                skipButton.style.opacity = '1'
                clearInterval(countdownInterval)
                debugLogger.info('Skip button is now available')
              }
            }, 1000)
          }

          this.player.one('playing', () => {
            startCountdown()
          })
        } else if (!this.allowSkip) {
          debugLogger.warn('Skip button disabled by allowSkip prop')
        } else {
          debugLogger.warn('XML missing event skip')
        }
      },

      setupMobileAutoplay() {
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        )

        if (isMobile) {
          debugLogger.info('Mobile device detected - setting up autoplay')
        }
      },

      async attemptAutoplay() {
        try {
          if (!this.player || !this.mediaUrl) {
            debugLogger.warn('Player or media URL not ready for autoplay')
            return
          }

          await new Promise((resolve) => {
            if (this.player.readyState() >= 2) {
              resolve()
            } else {
              this.player.one('canplay', resolve)
            }
          })

          this.player.muted(true)

          const playPromise = this.player.play()

          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                debugLogger.success('Autoplay successful')
              })
              .catch((error) => {
                debugLogger.warn('Autoplay failed, user interaction may be required', {
                  error: error.message,
                })
              })
          }
        } catch (error) {
          debugLogger.error('Error attempting autoplay', {
            error: error.message,
          })
        }
      },

      addMobilePlayButton() {
        debugLogger.info('Mobile play button disabled for always-on play')
      },

      setupPseudoFullscreen() {
        const shouldUsePseudoFullscreen = isDesktop()

        this.player.ready(() => {
          if (shouldUsePseudoFullscreen) {
            debugLogger.info('Using pseudo-fullscreen for desktop device', {
              userAgent: navigator.userAgent,
              fullscreenEnabled: document.fullscreenEnabled,
              isDesktop: isDesktop(),
            })

            const playerEl = this.player.el()
            playerEl.classList.add('pseudo-fullscreen')
            debugLogger.info('Automatically entering pseudo-fullscreen for desktop')

            this.$nextTick(() => {
              this.triggerFullscreenTracking()
            })

            this.player.one('play', () => {
              this.triggerFullscreenTracking()
            })

            if (this.player.controlBar && this.player.controlBar.fullscreenToggle) {
              this.player.controlBar.fullscreenToggle.off('click')
              this.player.controlBar.fullscreenToggle.on('click', () => {
                const isPseudoFullscreen = playerEl.classList.contains('pseudo-fullscreen')

                if (isPseudoFullscreen) {
                  playerEl.classList.remove('pseudo-fullscreen')
                  this.showFooterText = true

                  this.$nextTick(() => {
                    if (this.player && this.player.el()) {
                      const videoEl = this.player.el()

                      videoEl.style.opacity = '1'
                      videoEl.style.position = 'relative'
                      videoEl.style.top = 'auto'
                      videoEl.style.left = 'auto'
                      videoEl.style.width = '100%'
                      videoEl.style.height = ''
                      videoEl.style.zIndex = 'auto'
                      videoEl.style.display = 'block'

                      if (this.player && typeof this.player.dimensions === 'function') {
                        this.player.dimensions('100%', 'auto')
                      }

                      setTimeout(() => {
                        window.dispatchEvent(new Event('resize'))

                        // Log dimensions after resize
                        setTimeout(() => {
                          const finalComputedStyle = window.getComputedStyle(videoEl)
                        }, 100)
                      }, 50)

                      debugLogger.info('AFTER forcing styles in $nextTick', {
                        videoElClasses: videoEl.className,
                        videoElStyle: videoEl.style.cssText,
                        videoElDisplay: window.getComputedStyle(videoEl).display,
                        videoElOpacity: window.getComputedStyle(videoEl).opacity,
                        videoElPosition: window.getComputedStyle(videoEl).position,
                        videoElWidth: window.getComputedStyle(videoEl).width,
                        videoElHeight: window.getComputedStyle(videoEl).height,
                        videoElTop: window.getComputedStyle(videoEl).top,
                        videoElLeft: window.getComputedStyle(videoEl).left,
                        videoElVisible: videoEl.offsetParent !== null,
                      })
                    }
                  })
                } else {
                  playerEl.classList.add('pseudo-fullscreen')
                  this.showFooterText = true
                  debugLogger.info('Entering pseudo-fullscreen')

                  this.triggerFullscreenTracking()
                }
              })
            }
          } else {
            debugLogger.info('Using native fullscreen for mobile device', {
              userAgent: navigator.userAgent,
              fullscreenEnabled: document.fullscreenEnabled,
              isMobile: !isDesktop(),
            })
          }
        })
      },

      triggerFullscreenTracking() {
        if (this.trackingEvents?.fullscreen) {
          if (!this.fullscreenTrackingFired) {
            debugLogger.info(
              'Firing fullscreen events for pseudo-fullscreen',
              this.trackingEvents.fullscreen,
            )
            this.trackEvents(this.trackingEvents.fullscreen)
            this.fullscreenTrackingFired = true
          } else {
            debugLogger.info('Fullscreen tracking already fired, skipping duplicate')
          }
        } else {
          debugLogger.warn('Fullscreen tracking events not available yet', {
            hasTrackingEvents: !!this.trackingEvents,
            trackingEventsKeys: this.trackingEvents ? Object.keys(this.trackingEvents) : [],
          })
        }
      },

      triggerCompleteEvent() {
        if (this.trackingEvents && this.trackingEvents.complete) {
          debugLogger.info('Manually firing complete events', this.trackingEvents.complete)
          this.trackEvents(this.trackingEvents.complete)
        }
      },

      disableSeeking() {
        try {
          this.player.on('seeking', (e) => {
            e.preventDefault()
            e.stopImmediatePropagation()
            const currentTime = this.player.currentTime()
            if (this.player.seeking()) {
              this.player.currentTime(currentTime)
            }
            return false
          })

          this.player.on('seeked', (e) => {
            e.preventDefault()
            e.stopImmediatePropagation()
            return false
          })

          this.$nextTick(() => {
            const progressControl = this.player.el().querySelector('.vjs-progress-control')
            const progressHolder = this.player.el().querySelector('.vjs-progress-holder')
            const seekBar = this.player.el().querySelector('.vjs-seek-bar')

            if (progressControl) {
              progressControl.style.pointerEvents = 'none'
              progressControl.style.cursor = 'default'
            }

            if (progressHolder) {
              progressHolder.style.pointerEvents = 'none'
              progressHolder.style.cursor = 'default'
            }

            if (seekBar) {
              seekBar.style.pointerEvents = 'none'
              seekBar.style.cursor = 'default'
            }

            const progressElements = this.player
              .el()
              .querySelectorAll(
                '.vjs-progress-control, .vjs-progress-holder, .vjs-seek-bar, .vjs-play-progress, .vjs-load-progress',
              )
            progressElements.forEach((el) => {
              if (el) {
                el.style.pointerEvents = 'none'
                el.style.cursor = 'default'
                // Remove click handlers
                el.onclick = (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  return false
                }
                el.onmousedown = (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  return false
                }
                el.onmousemove = (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  return false
                }
              }
            })
          })
        } catch (error) {}
      },
    },
  }
</script>

<style scoped>
  .samba-vast-player {
    width: 100%;
    max-width: min(100%, 100vw);
    margin: 10px 0;
    position: relative;
    overflow: visible;
    box-sizing: border-box;
    background: transparent !important;
    background-color: transparent !important;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .video-container {
    width: 100%;
    max-width: 600px;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    margin: 0 auto;
  }

  .samba-vast-player > .video-container > .video-js {
    order: 2;
  }

  .advertising-overlay {
    position: relative;
    top: 0;
    left: 0;
    transform: none;
    z-index: 1;
    pointer-events: none;
    width: 100%;
    box-sizing: border-box;
    text-align: center;
    order: 1;
    margin-bottom: 10px;
  }

  .advertising-overlay .advertising-label {
    font-size: 14px;
    font-weight: 700;
    color: #202020;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: rgba(0, 0, 0, 0.6);
    padding: 4px 12px;
    border-radius: 4px;
    text-align: center;
    white-space: nowrap;
    display: inline-block;
  }

  /* Ensure overlay appears on top of video but doesn't block controls */
  .video-js {
    position: relative;
    z-index: 1;
  }

  .video-js .vjs-control-bar {
    z-index: 6;
    position: relative;
  }

  /* Hide overlay when video is loading */
  .samba-vast-player.video-loading .advertising-overlay {
    display: none !important;
  }

  .video-js:not(.pseudo-fullscreen) ~ .advertising-overlay {
    position: relative !important;
    top: 0 !important;
    left: 0 !important;
    transform: none !important;
    z-index: 1 !important;
    width: 100% !important;
    order: 1 !important;
    margin-bottom: 10px !important;
  }

  /* Fullscreen styles for advertising overlay */
  .video-js.pseudo-fullscreen ~ .advertising-overlay {
    position: fixed !important;
    top: 1vh !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    z-index: 10000 !important;
    margin-bottom: 5px !important;
  }

  body.pseudo-overlay-active .advertising-overlay {
    position: fixed !important;
    top: 1vh !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    z-index: 10000 !important;
    margin-bottom: 5px !important;
  }

  .player-footer-text {
    margin-top: 20px;
    font-size: 13px;
    font-weight: 400;
    color: #202020;
    text-transform: none;
    letter-spacing: 0.02em;
    text-align: center;
    width: 100%;
    padding: 0 15px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    position: relative;
    z-index: 1;
    order: 2;
  }

  .player-footer-text .advertising-label {
    font-size: 11px;
    font-weight: 500;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .player-footer-text .redirect-message {
    font-size: 13px;
    font-weight: 400;
    color: #202020;
  }

  .video-js:not(.pseudo-fullscreen) ~ .player-footer-text {
    position: relative !important;
    margin-top: 20px !important;
    order: 2 !important;
    bottom: auto !important;
    left: auto !important;
    transform: none !important;
  }

  .video-js.pseudo-fullscreen ~ .player-footer-text {
    position: fixed !important;
    top: calc(
      100vh - 1vh - 30px + 5px
    ) !important; /* Video bottom (100vh - 1vh - 30px) + spacing (5px) = position below video player */
    left: 50% !important;
    transform: translateX(-50%) !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    background: transparent !important;
    padding: 0 10px !important;
    border-radius: 0 !important;
    z-index: 10001 !important;
    pointer-events: none !important;
    font-size: 13px !important;
    font-weight: 400 !important;
    color: #202020 !important;
  }

  body.pseudo-overlay-active .player-footer-text {
    position: fixed !important;
    top: calc(
      100vh - 1vh - 30px + 5px
    ) !important; /* Video bottom (100vh - 1vh - 30px) + spacing (5px) = position below video player */
    left: 50% !important;
    transform: translateX(-50%) !important;
    width: auto !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    background: transparent !important;
    padding: 0 !important;
    border-radius: 0 !important;
    z-index: 10001 !important;
    pointer-events: none !important;
    font-size: 13px !important;
    font-weight: 400 !important;
    color: #202020 !important;
  }

  .samba-vast-player.video-loading {
    min-height: 400px;
    max-height: 400px;
    background: #000 !important;
    background-color: #000 !important;
  }

  .samba-vast-player.video-loading .video-js {
    min-height: 400px;
    max-height: 400px;
    background: #000 !important;
    background-color: #000 !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }

  /* Ensure video element itself is hidden while loading */
  .samba-vast-player.video-loading .video-js video {
    opacity: 0 !important;
    pointer-events: none !important;
  }

  /* Hide all controls while video is loading */
  .samba-vast-player.video-loading .video-js .vjs-control-bar,
  .samba-vast-player.video-loading .video-js .vjs-big-play-button,
  .samba-vast-player.video-loading .video-js .vjs-play-control,
  .samba-vast-player.video-loading .video-js .vjs-volume-panel,
  .samba-vast-player.video-loading .video-js .vjs-fullscreen-control,
  .samba-vast-player.video-loading .video-js .vjs-remaining-time,
  .samba-vast-player.video-loading .video-js .vjs-progress-control,
  .samba-vast-player.video-loading .video-js .vjs-control,
  .samba-vast-player.video-loading .video-js .vjs-button,
  .samba-vast-player.video-loading .vjs-skip-button {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }

  /* Hide footer text while video is loading */
  .samba-vast-player.video-loading .player-footer-text {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
  }

  .video-loading-placeholder {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    min-height: 400px;
    max-height: 400px;
    background: #000;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  }

  .video-loading-placeholder .loading-spinner {
    width: 50px;
    height: 50px;
    border: 4px solid rgba(255, 255, 255, 0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @media (max-width: 768px) {
    .samba-vast-player.video-loading {
      min-height: 400px;
      max-height: 400px;
    }

    .video-loading-placeholder {
      min-height: 400px;
      max-height: 400px;
    }
  }

  @media (max-width: 480px) {
    .samba-vast-player.video-loading {
      min-height: 400px;
      max-height: 400px;
    }

    .video-loading-placeholder {
      min-height: 400px;
      max-height: 400px;
    }
  }

  .video-js {
    width: 100% !important;
    max-width: min(100%, 100vw) !important;
    overflow: hidden !important;
    box-sizing: border-box !important;
    background: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
    position: relative !important;
  }

  /* Ensure video is visible when not loading */
  .samba-vast-player:not(.video-loading) .video-js {
    opacity: 1 !important;
  }

  /* Ensure video returns to normal state when NOT in pseudo-fullscreen */
  .video-js:not(.pseudo-fullscreen) {
    position: relative !important;
    opacity: 1 !important;
    top: auto !important;
    left: auto !important;
    width: 100% !important;
    min-height: 300px !important;
    z-index: auto !important;
    display: block !important;
    order: 2 !important;
  }

  /* Ensure the video element inside maintains aspect ratio */
  .video-js:not(.pseudo-fullscreen) video {
    width: 100% !important;
    height: auto !important;
    min-height: 300px !important;
    object-fit: contain !important;
  }

  .video-js .vjs-control-bar-wrapper {
    width: 100% !important;
    padding: 0 !important;
    margin: 0 !important;
    left: 0 !important;
    right: 0 !important;
    box-sizing: border-box !important;
  }

  .video-js .vjs-poster {
    background-color: transparent !important;
  }

  .video-js video {
    width: 100% !important;
    max-width: min(100%, 100vw) !important;
    object-fit: cover !important;
    box-sizing: border-box !important;
    display: block !important;
    background: transparent !important;
    background-color: transparent !important;
    mix-blend-mode: normal;
  }

  .video-js,
  .video-js *,
  .video-js .vjs-tech,
  .video-js .vjs-poster,
  .video-js .vjs-poster img,
  .video-js .vjs-big-play-button,
  .video-js .vjs-control-bar,
  .video-js .vjs-loading-spinner {
    background: transparent !important;
    background-color: transparent !important;
  }

  .video-js video {
    background: transparent !important;
    background-color: transparent !important;
  }

  @media (max-width: 768px) {
    .samba-vast-player {
      max-height: 75vh;
      background: transparent;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .video-js {
      background: transparent !important;
      max-height: 75vh;
    }

    .video-js.vjs-fluid {
      max-height: 75vh;
    }

    .video-js,
    .video-js *,
    .video-js .vjs-tech,
    .video-js .vjs-poster,
    .video-js .vjs-poster img,
    .video-js .vjs-big-play-button,
    .video-js .vjs-control-bar,
    .video-js .vjs-loading-spinner,
    .video-js video {
      background: transparent !important;
      background-color: transparent !important;
    }

    .video-js.vjs-fluid video {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      width: 100% !important;
      height: 100% !important;
      object-fit: cover !important;
      background: transparent !important;
    }
  }

  @media (max-width: 480px) {
    .samba-vast-player {
      max-height: 65vh;
      background: transparent;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .video-js {
      background: transparent !important;
      max-height: 65vh;
    }

    .video-js.vjs-fluid {
      max-height: 65vh;
    }

    .video-js,
    .video-js *,
    .video-js .vjs-tech,
    .video-js .vjs-poster,
    .video-js .vjs-poster img,
    .video-js .vjs-big-play-button,
    .video-js .vjs-control-bar,
    .video-js .vjs-loading-spinner,
    .video-js video {
      background: transparent !important;
      background-color: transparent !important;
    }

    .video-js.vjs-fluid video {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      width: 100% !important;
      height: 100% !important;
      object-fit: cover !important;
      background: transparent !important;
    }
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.9);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    border-radius: 4px;
  }

  .loading-spinner {
    width: 50px;
    height: 50px;
    border: 4px solid rgba(255, 255, 255, 0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .loading-text {
    color: #fff;
    margin-top: 15px;
    font-size: 14px;
    font-family: Arial, sans-serif;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* Video.js fluid mode will handle aspect ratio dynamically via player.aspectRatio() */

  .vjs-default-skin .vjs-big-play-button {
    font-size: 3em;
    line-height: 1.5em;
    height: 1.5em;
    width: 3em;
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    margin-left: -1.5em;
    margin-top: -0.75em;
  }

  /* VAST 2.0 click-through styling */
  .video-js.vjs-clickable {
    cursor: pointer;
  }

  .video-js.vjs-clickable:hover {
    cursor: pointer;
  }

  .video-js.vjs-clickable .vjs-poster {
    cursor: pointer;
  }

  /* Skip button styling */
  .vjs-skip-button {
    position: absolute;
    top: 10px;
    right: 10px;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    border: none;
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    z-index: 1000;
    font-size: 12px;
    transition: background-color 0.3s;
  }

  .vjs-skip-button:hover {
    background: rgba(0, 0, 0, 0.9);
  }

  .vjs-skip-button:disabled {
    background: rgba(0, 0, 0, 0.5);
    cursor: not-allowed;
    opacity: 0.7;
  }

  /* Hide picture-in-picture button for all devices */
  .video-js .vjs-picture-in-picture-control {
    display: none !important;
  }

  /* Samba info panel messaging */
  .samba-info {
    margin-top: 16px;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: center;
  }

  .samba-info-label {
    font-weight: 700;
    font-size: 14px;
    letter-spacing: 0.08em;
    padding: 6px 18px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.75);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .samba-info-message {
    font-size: 14px;
    line-height: 1.5;
    color: #fff;
    background: rgba(0, 0, 0, 0.75);
    padding: 10px 18px;
    border-radius: 12px;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    max-width: min(90vw, 520px);
  }

  .samba-info-message p {
    margin: 0;
  }

  .samba-info-message .emphasis {
    font-weight: 700;
    margin-top: 4px;
  }

  :global(body.pseudo-overlay-active) .samba-vast-player .samba-info {
    position: fixed;
    left: 50%;
    transform: translateX(-50%);
    bottom: 40px;
    z-index: 10001;
    pointer-events: none;
    width: min(90vw, 520px);
    gap: 12px;
  }

  :global(body.pseudo-overlay-active) .samba-vast-player .samba-info-label {
    margin-bottom: 0;
  }

  :global(body.pseudo-overlay-active) .samba-vast-player .samba-info-message {
    width: 100%;
  }

  /* Pseudo-fullscreen styles for captive portal devices */
  .video-js.pseudo-fullscreen {
    position: fixed !important;
    top: calc(
      1vh + 35px
    ) !important; /* Reserve space for header: 1vh top margin + ~35px header height */
    left: 2.5vw !important;
    width: 95vw !important;
    height: calc(
      100vh - 1vh - 35px - 1vh - 30px
    ) !important; /* Total height minus header space (1vh + 35px) and footer space (1vh + 30px) */
    max-height: calc(100vh - 1vh - 35px - 1vh - 30px) !important;
    min-height: 200px !important; /* Ensure minimum height for very small screens */
    z-index: 9999 !important;
    background: black !important;
    padding: 0 !important;
  }

  .video-js.pseudo-fullscreen video {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    object-fit: contain !important;
  }

  .video-js.pseudo-fullscreen .vjs-control-bar {
    position: absolute !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    z-index: 10000 !important;
    display: flex !important;
    align-items: center !important;
    visibility: visible !important;
    opacity: 1 !important;
    background: rgba(0, 0, 0, 0.85) !important;
    background-color: rgba(0, 0, 0, 0.85) !important;
    padding: 0 !important;
    height: 4.5em !important;
    flex-wrap: nowrap !important;
  }

  .vjs-error-display {
    display: none !important;
  }

  /* Black strip background for progress bar and controls */
  .video-js .vjs-control-bar {
    background: rgba(0, 0, 0, 0.85) !important;
    background-color: rgba(0, 0, 0, 0.85) !important;
    background-image: none !important;
    padding: 0 0 0 0 !important;
    margin: 0 !important;
    display: flex !important;
    align-items: center !important;
    height: 4.5em !important;
    width: 100% !important;
    left: 0 !important;
    right: 0 !important;
    box-sizing: border-box !important;
  }

  .video-js .vjs-control-bar > * {
    height: 30px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    box-sizing: border-box !important;
  }

  .video-js .vjs-progress-control {
    background: transparent !important;
    background-color: transparent !important;
    background-image: none !important;
    flex: 1 1 auto !important;
    height: auto !important;
    width: auto !important;
    display: flex !important;
    align-items: center !important;
    order: 0 !important;
    margin: -2px 0 0 0 !important;
    height: 30px !important;
    box-sizing: border-box !important;
    vertical-align: middle !important;
    pointer-events: none !important;
    cursor: default !important;
  }

  .video-js .vjs-progress-holder {
    background: rgba(0, 0, 0, 0.8) !important;
    background-color: rgba(0, 0, 0, 0.8) !important;
    background-image: none !important;
    height: 3px !important;
    margin: 0 10px !important;
    border-radius: 2px !important;
    position: relative !important;
    flex: 1 1 auto !important;
    align-self: center !important;
    vertical-align: middle !important;
    pointer-events: none !important;
    cursor: default !important;
  }

  .video-js .vjs-progress-holder::before {
    content: '' !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    background: rgba(0, 0, 0, 0.8) !important;
    border-radius: 2px !important;
  }

  .video-js .vjs-play-progress {
    background: #fff !important;
    background-color: #fff !important;
    background-image: none !important;
    height: 100% !important;
    border-radius: 2px !important;
  }

  .video-js .vjs-load-progress {
    background: rgba(255, 255, 255, 0.5) !important;
    background-color: rgba(255, 255, 255, 0.5) !important;
    background-image: none !important;
    height: 100% !important;
    border-radius: 2px !important;
  }

  .video-js .vjs-control {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    flex-shrink: 0 !important;
    margin: 0 !important;
  }

  .video-js .vjs-button {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    flex-shrink: 0 !important;
    margin: 0 !important;
    vertical-align: middle !important;
    height: 30px !important;
  }

  .video-js .vjs-play-control,
  .video-js .vjs-play-button,
  .video-js .vjs-mute-control,
  .video-js .vjs-volume-panel,
  .video-js .vjs-time-control,
  .video-js .vjs-remaining-time,
  .video-js .vjs-fullscreen-control,
  .video-js .vjs-fullscreen-toggle {
    margin: 0 !important;
    vertical-align: middle !important;
    display: flex !important;
    align-items: center !important;
    height: 30px !important;
  }

  .video-js .vjs-control-bar > *:first-child {
    margin-left: 0 !important;
    padding-left: 0 !important;
  }

  .video-js .vjs-control-bar > *:last-child {
    margin-right: 0 !important;
    padding-right: 0 !important;
  }

  /* Hide extra controls - keep only essential ones */
  .video-js .vjs-playback-rate,
  .video-js .vjs-playback-rate-menu-button,
  .video-js .vjs-chapters-button,
  .video-js .vjs-descriptions-button,
  .video-js .vjs-subs-caps-button,
  .video-js .vjs-audio-track-button,
  .video-js .vjs-live-control,
  .video-js .vjs-live-display,
  .video-js .vjs-seek-to-live-control,
  .video-js .vjs-current-time,
  .video-js .vjs-time-divider,
  .video-js .vjs-duration,
  .video-js .vjs-menu-button,
  .video-js .vjs-custom-control-spacer {
    display: none !important;
  }

  .video-js .vjs-play-progress::before {
    background: #fff !important;
    background-color: #fff !important;
  }

  .video-js .vjs-slider {
    background: rgba(0, 0, 0, 0.8) !important;
    background-color: rgba(0, 0, 0, 0.8) !important;
    background-image: none !important;
    pointer-events: none !important;
    cursor: default !important;
  }

  .video-js .vjs-seek-bar,
  .samba-vast-player .video-js .vjs-seek-bar {
    pointer-events: none !important;
    cursor: default !important;
  }
</style>

<style>
  /* Non-scoped styles for Video.js dynamically created elements */
  .samba-vast-player .video-js {
    padding: 0 !important;
    margin: 0 !important;
  }

  .samba-vast-player .video-js .vjs-control-bar-wrapper {
    width: 100% !important;
    padding: 0 !important;
    margin: 0 !important;
    left: 0 !important;
    right: 0 !important;
    box-sizing: border-box !important;
  }

  .samba-vast-player .video-js .vjs-control-bar {
    background: rgba(0, 0, 0, 0.85) !important;
    background-color: rgba(0, 0, 0, 0.85) !important;
    background-image: none !important;
    padding: 0 0 0 0 !important;
    margin: 0 !important;
    display: flex !important;
    align-items: center !important;
    height: 4.5em !important;
    flex-wrap: nowrap !important;
    width: 100% !important;
    left: 0 !important;
    right: 0 !important;
    box-sizing: border-box !important;
    position: absolute !important;
  }

  .samba-vast-player .video-js .vjs-control-bar > * {
    height: 30px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    box-sizing: border-box !important;
  }

  .samba-vast-player .video-js .vjs-control-bar > * {
    display: flex !important;
    align-items: center !important;
    flex-shrink: 0 !important;
  }

  .samba-vast-player .video-js .vjs-progress-control {
    background: transparent !important;
    background-color: transparent !important;
    background-image: none !important;
    flex: 1 1 auto !important;
    height: auto !important;
    display: flex !important;
    align-items: center !important;
    order: 0 !important;
    min-width: 0 !important;
    margin: -2px 0 0 0 !important;
    height: 30px !important;
    box-sizing: border-box !important;
    vertical-align: middle !important;
    pointer-events: none !important;
    cursor: default !important;
  }

  .samba-vast-player .video-js .vjs-progress-holder {
    background: rgba(0, 0, 0, 0.9) !important;
    background-color: rgba(0, 0, 0, 0.9) !important;
    background-image: none !important;
    height: 3px !important;
    margin: 0 10px !important;
    border-radius: 2px !important;
    position: relative !important;
    flex: 1 1 auto !important;
    align-self: center !important;
    min-width: 0 !important;
    vertical-align: middle !important;
    pointer-events: none !important;
    cursor: default !important;
  }

  .samba-vast-player .video-js .vjs-play-progress {
    background: #fff !important;
    background-color: #fff !important;
    background-image: none !important;
    height: 100% !important;
    border-radius: 2px !important;
    pointer-events: none !important;
    cursor: default !important;
  }

  .samba-vast-player .video-js .vjs-load-progress {
    background: rgba(255, 255, 255, 0.5) !important;
    background-color: rgba(255, 255, 255, 0.5) !important;
    background-image: none !important;
    height: 100% !important;
    border-radius: 2px !important;
    pointer-events: none !important;
    cursor: default !important;
  }

  .samba-vast-player .video-js .vjs-control {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    flex-shrink: 0 !important;
    margin: 0 !important;
  }

  .samba-vast-player .video-js .vjs-button {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    flex-shrink: 0 !important;
    margin: 0 !important;
    vertical-align: middle !important;
    height: 30px !important;
  }

  .samba-vast-player .video-js .vjs-time-control {
    display: flex !important;
    align-items: center !important;
    flex-shrink: 0 !important;
    margin: 0 !important;
    vertical-align: middle !important;
  }

  .samba-vast-player .video-js .vjs-play-control,
  .samba-vast-player .video-js .vjs-play-button,
  .samba-vast-player .video-js .vjs-mute-control,
  .samba-vast-player .video-js .vjs-volume-panel,
  .samba-vast-player .video-js .vjs-remaining-time,
  .samba-vast-player .video-js .vjs-fullscreen-control,
  .samba-vast-player .video-js .vjs-fullscreen-toggle {
    margin: 0 !important;
    vertical-align: middle !important;
    display: flex !important;
    align-items: center !important;
    height: 30px !important;
  }

  .samba-vast-player .video-js .vjs-control-bar > *:first-child {
    margin-left: 0 !important;
    padding-left: 0 !important;
  }

  .samba-vast-player .video-js .vjs-control-bar > *:last-child {
    margin-right: 0 !important;
    padding-right: 0 !important;
  }

  /* Hide extra controls - keep only essential ones */
  .samba-vast-player .video-js .vjs-playback-rate,
  .samba-vast-player .video-js .vjs-playback-rate-menu-button,
  .samba-vast-player .video-js .vjs-chapters-button,
  .samba-vast-player .video-js .vjs-descriptions-button,
  .samba-vast-player .video-js .vjs-subs-caps-button,
  .samba-vast-player .video-js .vjs-audio-track-button,
  .samba-vast-player .video-js .vjs-live-control,
  .samba-vast-player .video-js .vjs-live-display,
  .samba-vast-player .video-js .vjs-seek-to-live-control,
  .samba-vast-player .video-js .vjs-current-time,
  .samba-vast-player .video-js .vjs-time-divider,
  .samba-vast-player .video-js .vjs-duration,
  .samba-vast-player .video-js .vjs-menu-button,
  .samba-vast-player .video-js .vjs-custom-control-spacer {
    display: none !important;
  }

  .samba-vast-player .video-js .vjs-slider {
    background: rgba(0, 0, 0, 0.9) !important;
    background-color: rgba(0, 0, 0, 0.9) !important;
    background-image: none !important;
  }

  .samba-vast-player .video-js.pseudo-fullscreen .vjs-control-bar {
    background: rgba(0, 0, 0, 0.85) !important;
    background-color: rgba(0, 0, 0, 0.85) !important;
    background-image: none !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
    height: 4.5em !important;
    flex-wrap: nowrap !important;
  }
</style>
